import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Collapse,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  CheckCircle,
  Clear,
  Description,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import {
  FaAmazon,
  FaFileExcel,
  FaDownload,
  FaFileArchive,
  FaImages
} from 'react-icons/fa';
import { useActivityLog } from '../context/ActivityLogContext';
import API_BASE_URL, { apiCall } from '../config/api';
import { formatLogTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS } from '../constants/colors';

const AmazonBulkImageUploader = () => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const asinFileInputRef = useRef(null);

  // Product type toggle: rugs, furniture, lighting
  const [productType, setProductType] = useState('rug');

  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // ASIN file management
  const [asinFileExists, setAsinFileExists] = useState(false);
  const [asinUploading, setAsinUploading] = useState(false);
  const [showAsinSection, setShowAsinSection] = useState(false);

  // Shot type mapping reference
  const [showMappings, setShowMappings] = useState(false);

  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  // Check if ASIN file exists on load
  useEffect(() => {
    const checkAsinFile = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/AmazonBulkImageUploader/check-asin`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAsinFileExists(data.exists === true);
        } else {
          setAsinFileExists(false);
        }
      } catch {
        setAsinFileExists(false);
      }
    };
    checkAsinFile();
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (processIdRef.current) {
          clearIntervalRef(processIdRef.current);
        }
      }
    };
  }, [clearIntervalRef]);

  const fetchStatus = async (processId) => {
    try {
      const response = await apiCall(`${API_BASE_URL}/api/AmazonBulkImageUploader/status/${processId}`);

      if (response.logs && response.logs.length > 0) {
        setLogs(processId, response.logs);
      }

      if (response.progressPercentage !== undefined) {
        setProgress(response.progressPercentage);
        updateActivityProcess(processId, { progress: response.progressPercentage });
      }

      if (response.status === 'completed') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }

        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          await downloadFile(processId, response.fileName, response.awsUrl);
        }

        setResults({
          success: true,
          itemsProcessed: response.processedItems || 0,
          imagesDownloaded: response.imagesDownloaded || 0,
          itemsSkipped: response.itemsSkipped || 0,
          zipFileSize: response.zipFileSize || null,
          fileName: response.fileName
        });
        completeActivityProcess(processId, 'success', 'Processing completed successfully');
        setSuccess(
          response.itemsSkipped > 0
            ? `Processing complete! ${response.itemsSkipped} items were skipped (no ASIN found).`
            : 'Processing complete! Zip file with renamed images has been generated.'
        );
        setLoading(false);
        setProgress(null);
      } else if (response.status === 'error') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }
        setError(`Error: ${response.message}`);
        setLoading(false);
        setProgress(null);
        completeActivityProcess(processId, 'error', response.message);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const downloadFile = async (processId, serverFileName, awsUrl) => {
    try {
      addLog(processId, 'Downloading zip file...');

      // Primary: download from S3 URL (survives app pool recycles)
      if (awsUrl) {
        const link = document.createElement('a');
        link.href = awsUrl;
        link.download = serverFileName || `Amazon Bulk Images ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).replace(/,/g, '').replace(/:/g, '-')}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        addLog(processId, 'Download complete!');
        return;
      }

      // Fallback: download from in-memory endpoint
      const response = await fetch(`${API_BASE_URL}/api/AmazonBulkImageUploader/download/${processId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = serverFileName || `Amazon Bulk Images ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).replace(/,/g, '').replace(/:/g, '-')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addLog(processId, 'Download complete!');
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(processId, `Download error: ${err.message}`);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/AmazonBulkImageUploader/sample`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download template file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      link.download = `Amazon Bulk Image Uploader Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download template file.');
    }
  };

  // ASIN file handlers
  const handleUploadAsinFile = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.xlsx')) {
      setError('ASIN file must be an .xlsx file.');
      return;
    }

    setAsinUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_BASE_URL}/api/AmazonBulkImageUploader/upload-asin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload ASIN file');
      }

      setAsinFileExists(true);
      setSuccess('ASIN lookup file uploaded successfully.');
    } catch (err) {
      setError(err.message || 'Failed to upload ASIN file.');
    }

    setAsinUploading(false);
    if (asinFileInputRef.current) asinFileInputRef.current.value = '';
  };

  const handleDownloadAsinFile = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/AmazonBulkImageUploader/download-asin`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download ASIN file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ASIN_Lookup.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download ASIN file.');
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const droppedFile = droppedFiles[0];
    if (droppedFile && droppedFile.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
      setFile(droppedFile);
      setError('');
    } else {
      setError('Please drop an Excel or CSV file only (.xlsx, .xls, .csv)');
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
        setError('Please select an Excel or CSV file only.');
        return;
      }
      setFile(selectedFile);
      setError('');
      setSuccess('');
    }
  };

  const handleProcess = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(0);
    downloadTriggeredRef.current = false;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('productType', productType);

      const response = await apiCall(`${API_BASE_URL}/api/AmazonBulkImageUploader/process`, {
        method: 'POST',
        body: formData
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        const typeLabel = productType === 'rug' ? 'Rugs' : productType === 'furniture' ? 'Furniture' : 'Lighting';
        addActivityProcess(serverProcessId, `Amazon Bulk Image Upload (${typeLabel})`, 'Amazon Bulk Image Uploader');
        addLog(serverProcessId, 'Starting processing...');

        intervalRef.current = setInterval(() => fetchStatus(serverProcessId), 1000);
        setIntervalRef(serverProcessId, intervalRef.current);
      }
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'An error occurred during processing. Please try again.');
      setLoading(false);
      setProgress(null);
    }
  };

  const resetForm = () => {
    setFile(null);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getProductTypeDescription = () => {
    switch (productType) {
      case 'rug':
        return 'Upload Excel output from the Jeff image links tool for rugs. The tool downloads images from URLs, looks up ASINs, renames files to Amazon\'s {ASIN}.{SHOT_TYPE}.jpg format, and packages them into a zip file for bulk upload.';
      case 'furniture':
        return 'Upload Excel output from the Jeff image links tool for furniture. Uses furniture-specific shot type mapping (ItemImage/FRONT as MAIN, ROOMAI as FRNT, DIM as PT01) and packages renamed images into a zip file.';
      case 'lighting':
        return 'Upload Excel output from the Jeff image links tool for lighting. Uses lighting-specific shot type mapping (ROOMAI as MAIN, DIM as PT01, OFF as PT02) with auto-fill for remaining slots, and packages renamed images into a zip file.';
      default:
        return '';
    }
  };

  const getChipLabel = () => {
    switch (productType) {
      case 'rug':
        return 'ROOMAI/ROOM \u2192 MAIN, FRONT \u2192 FRNT';
      case 'furniture':
        return 'FRONT \u2192 MAIN, ROOMAI \u2192 FRNT';
      case 'lighting':
        return 'ROOMAI \u2192 MAIN, DIM \u2192 PT01, OFF \u2192 PT02';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Amazon Bulk Image Uploader" category="link" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          <FaAmazon style={{ marginRight: 8, verticalAlign: 'middle' }} />
          AMAZON BULK IMAGE UPLOADER
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Upload & Process
              </Typography>

              {/* Product Type Toggle */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Product Type:
                </Typography>
                <ToggleButtonGroup
                  value={productType}
                  exclusive
                  onChange={(e, newType) => { if (newType) { setProductType(newType); resetForm(); } }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      '&.Mui-selected': {
                        bgcolor: TOOL_COLORS.LINK.primary,
                        color: '#fff',
                        '&:hover': { bgcolor: TOOL_COLORS.LINK.hover },
                      },
                    },
                  }}
                >
                  <ToggleButton value="rug">Rugs</ToggleButton>
                  <ToggleButton value="furniture">Furniture</ToggleButton>
                  <ToggleButton value="lighting">Lighting</ToggleButton>
                </ToggleButtonGroup>
                <Chip
                  label={getChipLabel()}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 11 }}
                />
              </Box>

              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                {getProductTypeDescription()}
              </Typography>

              {/* ASIN File Management Section */}
              <Card sx={{ mb: 3, bgcolor: 'transparent' }}>
                <CardContent sx={{ pb: '16px !important' }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: showAsinSection ? 2 : 0 }}
                    onClick={() => setShowAsinSection(!showAsinSection)}
                  >
                    <FaFileExcel color={TOOL_COLORS.LINK.primary} size={16} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary, flex: 1 }}>
                      ASIN Lookup File
                    </Typography>
                    <Chip
                      label={asinFileExists ? 'ASIN file uploaded' : 'No ASIN file'}
                      size="small"
                      sx={{
                        mr: 1,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: asinFileExists ? alpha('#4CAF50', 0.12) : alpha('#f44336', 0.12),
                        color: asinFileExists ? '#4CAF50' : '#f44336',
                        border: `1px solid ${asinFileExists ? alpha('#4CAF50', 0.3) : alpha('#f44336', 0.3)}`
                      }}
                    />
                    {showAsinSection ? <ExpandLess /> : <ExpandMore />}
                  </Box>

                  <Collapse in={showAsinSection}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                      Upload an ASIN lookup file (.xlsx) that maps Item IDs to Amazon ASINs. This file is required for renaming images to the {'{ASIN}.{SHOT_TYPE}.jpg'} format.
                    </Typography>

                    {/* Hidden ASIN file input */}
                    <input
                      type="file"
                      ref={asinFileInputRef}
                      onChange={handleUploadAsinFile}
                      accept=".xlsx"
                      style={{ display: 'none' }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => asinFileInputRef.current?.click()}
                        disabled={asinUploading}
                        sx={{
                          bgcolor: TOOL_COLORS.LINK.primary,
                          '&:hover': { bgcolor: TOOL_COLORS.LINK.hover },
                          textTransform: 'none'
                        }}
                        startIcon={<CloudUpload />}
                      >
                        {asinUploading ? 'Uploading...' : 'Upload ASIN File'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleDownloadAsinFile}
                        disabled={!asinFileExists}
                        sx={{
                          borderColor: TOOL_COLORS.LINK.primary,
                          color: TOOL_COLORS.LINK.primary,
                          '&:hover': {
                            borderColor: TOOL_COLORS.LINK.hover,
                            bgcolor: TOOL_COLORS.LINK.light
                          },
                          textTransform: 'none'
                        }}
                        startIcon={<FaDownload size={12} />}
                      >
                        Download Current ASIN File
                      </Button>
                    </Box>

                    {asinUploading && (
                      <LinearProgress sx={{ mt: 1, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.LINK.primary } }} />
                    )}
                  </Collapse>
                </CardContent>
              </Card>

              {/* File Upload Area */}
              <Card sx={{ mb: 3, bgcolor: file ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.LINK.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Excel / CSV File (Jeff Image Links Output)
                    </Typography>
                    {file && (
                      <Tooltip title="1 file selected">
                        <CheckCircle sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                  />

                  {/* Drag & Drop Zone */}
                  <Box
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.LINK.primary : file ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragging
                        ? TOOL_COLORS.LINK.light
                        : file
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: file ? '#4CAF50' : TOOL_COLORS.LINK.primary,
                        bgcolor: file
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : TOOL_COLORS.LINK.light,
                      }
                    }}
                  >
                    {!file ? (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.LINK.primary : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {isDragging
                            ? 'Drop file here'
                            : 'Drop your Excel or CSV file here or click to browse'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Excel (.xlsx, .xls) or CSV (.csv) files supported
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          1 file selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click or drop to replace
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* File Info */}
                  {file && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                          borderColor: TOOL_COLORS.LINK.primary,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: TOOL_COLORS.LINK.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({formatFileSize(file.size)})
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleProcess}
                  disabled={!file || loading}
                  sx={{
                    bgcolor: TOOL_COLORS.LINK.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.LINK.hover },
                    px: 4
                  }}
                  startIcon={<FaAmazon />}
                >
                  {loading ? 'Processing...' : 'Process'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleDownloadTemplate}
                  disabled={loading}
                  sx={{
                    borderColor: TOOL_COLORS.LINK.primary,
                    color: TOOL_COLORS.LINK.primary,
                    '&:hover': {
                      borderColor: TOOL_COLORS.LINK.hover,
                      bgcolor: TOOL_COLORS.LINK.light
                    }
                  }}
                  startIcon={<FaDownload />}
                >
                  Download Template
                </Button>

                {(file || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={loading}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Info Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {loading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Processing file...
                  </Typography>
                  <LinearProgress
                    variant={progress > 0 ? "determinate" : "indeterminate"}
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: TOOL_COLORS.LINK.primary,
                        borderRadius: 4,
                      }
                    }}
                  />
                  {progress > 0 && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                      {Math.round(progress)}% complete
                    </Typography>
                  )}
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <FaAmazon color={TOOL_COLORS.LINK.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.itemsProcessed || 0} Items Processed`}
                        secondary="Items with ASIN lookup matched"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FaImages color="#4CAF50" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.imagesDownloaded || 0} Images Downloaded`}
                        secondary="Images renamed to {ASIN}.{SHOT_TYPE}.jpg"
                      />
                    </ListItem>
                    {results.itemsSkipped > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <Clear sx={{ color: '#f44336' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${results.itemsSkipped} Items Skipped`}
                          secondary="No ASIN found in lookup file"
                        />
                      </ListItem>
                    )}
                    {results.zipFileSize && (
                      <ListItem>
                        <ListItemIcon>
                          <FaFileArchive color={TOOL_COLORS.LINK.primary} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Zip File: ${formatFileSize(results.zipFileSize)}`}
                          secondary="Ready for Amazon upload"
                        />
                      </ListItem>
                    )}
                  </List>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Zip file has been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Upload an ASIN lookup file (if not already uploaded)<br />
                2. Select the correct product type (Rugs/Furniture/Lighting)<br />
                3. Download the template file for reference<br />
                4. Upload Excel output from the Jeff image links tool<br />
                5. Click "Process" to start<br />
                6. Zip file with renamed images will auto-download
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Shot Type Mapping Reference */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: showMappings ? 2 : 0 }}
                onClick={() => setShowMappings(!showMappings)}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                  Shot Type Mappings ({productType === 'rug' ? 'Rugs' : productType === 'furniture' ? 'Furniture' : 'Lighting'})
                </Typography>
                {showMappings ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </Box>

              <Collapse in={showMappings}>
                {productType === 'rug' && (
                  <Box sx={{ fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { color: theme.palette.text.primary } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>
                      Rugs Shot Type Mapping
                    </Typography>
                    <Box sx={{ pl: 1, mb: 1, fontSize: 11, fontFamily: 'monospace', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 1, p: 1.5, maxHeight: 300, overflow: 'auto' }}>
                      <strong>MAIN:</strong> ROOMAI/1-10 &rarr; MAIN | ROOM &rarr; MAIN (fallback)<br />
                      <strong>FRNT:</strong> ItemImage/FRONT &rarr; FRNT<br />
                      <strong>SIDE:</strong> SIDE &rarr; SIDE (auto-populate)<br />
                      <strong>BACK:</strong> ROOMBACK &rarr; BACK<br />
                      <strong>PT01-05:</strong> DETAIL1-5 &rarr; PT01-05<br />
                      <strong>PT06:</strong> Leftover ROOMAI &rarr; PT06
                    </Box>
                  </Box>
                )}

                {productType === 'furniture' && (
                  <Box sx={{ fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { color: theme.palette.text.primary } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>
                      Furniture Shot Type Mapping
                    </Typography>
                    <Box sx={{ pl: 1, mb: 1, fontSize: 11, fontFamily: 'monospace', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 1, p: 1.5, maxHeight: 300, overflow: 'auto' }}>
                      <strong>MAIN:</strong> ItemImage/FRONT &rarr; MAIN<br />
                      <strong>FRNT:</strong> ROOMAI/1-10 &rarr; FRNT | ROOM &rarr; FRNT (fallback)<br />
                      <strong>SIDE:</strong> SIDE &rarr; SIDE<br />
                      <strong>BACK:</strong> BACK &rarr; BACK<br />
                      <strong>PT01:</strong> DIM &rarr; PT01<br />
                      <strong>PT02-05:</strong> DETAIL1-4 &rarr; PT02-05<br />
                      <strong>PT06:</strong> Leftover &rarr; PT06
                    </Box>
                  </Box>
                )}

                {productType === 'lighting' && (
                  <Box sx={{ fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { color: theme.palette.text.primary } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>
                      Lighting Shot Type Mapping
                    </Typography>
                    <Box sx={{ pl: 1, mb: 1, fontSize: 11, fontFamily: 'monospace', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 1, p: 1.5, maxHeight: 300, overflow: 'auto' }}>
                      <strong>MAIN:</strong> ROOMAI/1-10 &rarr; MAIN | ROOM &rarr; MAIN (fallback)<br />
                      <strong>FRNT:</strong> ItemImage/FRONT &rarr; FRNT<br />
                      <strong>PT01:</strong> DIM &rarr; PT01<br />
                      <strong>PT02:</strong> OFF &rarr; PT02<br />
                      <strong>Auto-fill:</strong> SIDE, BACK, PT03-06
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Show all three mappings for quick reference */}
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                  Tip: Switch the product type toggle above to see the mapping for each category.
                </Typography>
              </Collapse>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AmazonBulkImageUploader;
